import React, { useState } from 'react';
import {
  HStack,
  ButtonGroup,
  InputGroup,
  InputLeftElement,
  Input,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Tooltip,
  Box,
  Flex,
  Text,
  Badge,
} from '@chakra-ui/react';
import { SearchIcon, AddIcon, ChevronDownIcon } from '@chakra-ui/icons';
import moment from 'moment';

/**
 * Filters component for TanStack Kanban implementation
 * @param {Object} props - Component props
 * @param {string} props.searchTerm - Current search query
 * @param {Function} props.onSearch - Function to update search query
 * @param {Object} props.currentFilters - Current filters object
 * @param {Function} props.onFilterChange - Function to update filters
 * @returns {React.ReactElement} - Filters component
 */
const TanFilters = ({
  searchTerm = '',
  onSearch,
  currentFilters = {},
  onFilterChange,
}) => {
  // Extract values from currentFilters with defaults
  const {
    callFilter = 'all',
    tagFilter = '',
    assigneeFilter = '',
    missedFollowUpFilter = false,
    todayFollowUpFilter = false,
    dateFilter = {}
  } = currentFilters;
  
  // Create handler functions for filter changes
  const handleCallFilterChange = (value) => {
    onFilterChange({ callFilter: value });
  };
  
  const handleTagFilterChange = (value) => {
    onFilterChange({ tagFilter: value });
  };
  
  const handleAssigneeFilterChange = (value) => {
    onFilterChange({ assigneeFilter: value });
  };
  
  const handleMissedFollowUpChange = (value) => {
    onFilterChange({ 
      missedFollowUpFilter: value,
      todayFollowUpFilter: value ? false : todayFollowUpFilter
    });
  };
  
  const handleTodayFollowUpChange = (value) => {
    onFilterChange({ 
      todayFollowUpFilter: value,
      missedFollowUpFilter: value ? false : missedFollowUpFilter
    });
  };
  
  const handleClearAllFilters = () => {
    onFilterChange({
      callFilter: 'all',
      tagFilter: '',
      assigneeFilter: '',
      missedFollowUpFilter: false,
      todayFollowUpFilter: false,
      dateFilter: {},
      search: ''
    });
    if (onSearch) onSearch('');
  };

  // Helper function to get call filter display text
  const getCallFilterDisplay = () => {
    switch(callFilter) {
      case 'no_calls': return 'No Calls';
      case 'has_calls': return 'Has Calls';
      case 'all': return 'All Calls';
      default: return 'All Calls';
    }
  };

  // Helper function to format date range display
  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return '';
    return `${moment(startDate).format('MMM D')} - ${moment(endDate).format('MMM D')}`;
  };
  
  // Handle add new lead (placeholder)
  const handleAddNewLead = () => {
    console.log('Add new lead');
    // Implement this functionality or pass from parent
  };

  return (
    <>
      <HStack spacing={2} mb={2} flexWrap="wrap" width="100%">
        <ButtonGroup size="sm" isAttached variant="outline" spacing={0}>
          {/* Search input */}
          <InputGroup size="sm" width="auto" minW="140px" maxW="200px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => onSearch(e.target.value)}
              borderRadius="md"
            />
          </InputGroup>

          {/* Calls filter */}
          <Menu>
            <MenuButton as={Button} size="sm" rightIcon={<ChevronDownIcon />}>
              {getCallFilterDisplay()}
            </MenuButton>
            <MenuList fontSize="sm">
              <MenuItem onClick={() => handleCallFilterChange('all')}>All Calls</MenuItem>
              <MenuItem onClick={() => handleCallFilterChange('has_calls')}>Has Calls</MenuItem>
              <MenuItem onClick={() => handleCallFilterChange('no_calls')}>No Calls</MenuItem>
            </MenuList>
          </Menu>

          {/* Follow-up filters */}
          <ButtonGroup size="sm" isAttached spacing={0}>
            <Button
              size="sm"
              variant="outline"
              colorScheme={missedFollowUpFilter ? "red" : "gray"}
              onClick={() => handleMissedFollowUpChange(!missedFollowUpFilter)}
            >
              Missed Follow-ups
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorScheme={todayFollowUpFilter ? "green" : "gray"}
              onClick={() => handleTodayFollowUpChange(!todayFollowUpFilter)}
            >
              Today's Follow-ups
            </Button>
          </ButtonGroup>

          {/* Tags filter */}
          <Menu>
            <MenuButton as={Button} size="sm" rightIcon={<ChevronDownIcon />}>
              {tagFilter || 'Tags'}
            </MenuButton>
            <MenuList fontSize="sm">
              <MenuItem onClick={() => handleTagFilterChange('')}>All Tags</MenuItem>
              <MenuItem onClick={() => handleTagFilterChange("no_tags")}>No Tags</MenuItem>
              <MenuDivider />
              {['Hot', 'Not Responding', 'Fake Lead', 'Inquiring', 'Low Budget', 'Venues Pending', 'Proposal Pending', 'Discuss'].map(tag => (
                <MenuItem key={tag} onClick={() => handleTagFilterChange(tag)}>{tag}</MenuItem>
              ))}
            </MenuList>
          </Menu>

          {/* Add New Lead Button */}
          <Tooltip label="Add New Lead" hasArrow>
            <Button
              size="sm"
              colorScheme="blue"
              leftIcon={<AddIcon boxSize={3} />}
              onClick={handleAddNewLead}
            >
              Add
            </Button>
          </Tooltip>
        </ButtonGroup>
      </HStack>

      {/* Active Filters display */}
      {(searchTerm || callFilter !== 'all' || tagFilter || assigneeFilter || missedFollowUpFilter || todayFollowUpFilter || (currentFilters.dateFilter && currentFilters.dateFilter.startDate)) && (
        <Box mb={1}>
          <Flex wrap="wrap" gap={1} alignItems="center">
            <Text fontSize="2xs" color="gray.600" fontWeight="medium">Filters:</Text>
            {searchTerm && (
              <Badge colorScheme="blue" variant="subtle" fontSize="2xs" py={0}>
                {searchTerm}
              </Badge>
            )}
            {callFilter && callFilter !== 'all' && (
              <Badge colorScheme="blue" variant="subtle" fontSize="2xs" py={0}>
                {getCallFilterDisplay()}
              </Badge>
            )}
            {tagFilter && (
              <Badge colorScheme="purple" variant="subtle" fontSize="2xs" py={0}>
                {tagFilter}
              </Badge>
            )}
            {assigneeFilter && (
              <Badge colorScheme="cyan" variant="subtle" fontSize="2xs" py={0}>
                Assigned: {assigneeFilter}
              </Badge>
            )}
            {missedFollowUpFilter && (
              <Badge colorScheme="red" variant="subtle" fontSize="2xs" py={0}>
                Missed Follow-ups
              </Badge>
            )}
            {todayFollowUpFilter && (
              <Badge colorScheme="green" variant="subtle" fontSize="2xs" py={0}>
                Today's Follow-ups
              </Badge>
            )}
            {currentFilters.dateFilter && currentFilters.dateFilter.startDate && currentFilters.dateFilter.endDate && (
              <Badge colorScheme="orange" variant="subtle" fontSize="2xs" py={0}>
                {formatDateRange(currentFilters.dateFilter.startDate, currentFilters.dateFilter.endDate)}
              </Badge>
            )}
            <Button size="2xs" variant="ghost" colorScheme="gray" onClick={handleClearAllFilters}>
              Clear filters
            </Button>
          </Flex>
        </Box>
      )}
    </>
  );
};

export default TanFilters;
